// @ts-check
'use_strict';

declare var $;
declare var Tygh;

import { resize_last_dialog } from '../tv_utils/utils.js';
import { StateSelectorView } from '../tv_product_quotes/product_quotes';
import { TVModal } from '../tv_utils/tv_modal';
import { AdvancedSelect } from '../tv_utils/advanced_select';
import { Notifications } from '../tv_utils/notifications';
import { config } from '../tv_utils/global_config_data.js';
import { ConfigArtworkUploader } from '../tv_artwork/config_artwork_uploader';
import { ConfirmDialog } from '../tv_utils/confirm_dialog';
import { StockStatus } from './components/stock_status';
import { PriceUpdater } from './components/price_updater';
import { Tabs } from './components/tabs';
import { ImageValidation } from './components/image_validation';
import { DIFUUploadQueue } from '../tv_artwork/difu_upload_queue';
import { init_g_recaptcha } from '../tv_third_party_integrations/g_recaptcha';
import { StickyCTA } from '../tv_utils/sticky_cta';
import { YardLetterPreview } from './components/yard_letter_preview';
import { CartView } from './CartView.js';
import { ProductPageLoader } from './ProductPageLoader.js';

/**
 * Class to handle binding controls and updates for the configurator UI.
 *
 * @property {boolean} config_in_progress flag to track if an ajax request is pending response from the configurator.
 * @property {Object<string, any>[]} queue for configuration changes to make requests for when the pending one responds.
 * @property {number} timeout interval for the configuration before an ajax request is sent to keep the connection open.
 * @property {number} throttle interval to stop from firing ajax requests while the user is still using the ui control.
 * @property {string} configuration_type used to disable configurator interaction for hardware products.
 * @property {string} smart_code string for the product configuration.
 * @property {boolean} quick_view lets us know that this configuration is loaded in a quick view modal.
 * @property {boolean} reconfig lets us know that this is editing a previous configuration.
 * @property {string} modal_suffix string at the end of the Element ids to identify which elements are for this config.
 * @property {boolean} can_upload_artwork flag to track if the Artwork Uploader is available.
 * @property {boolean} artwork_required flag to trak if Artwork is required for this product.
 * @property {boolean} added_to_cart flag to track whether configuration has been added to the cart.
 */
export class Configurator {

    /**
     * @var {boolean} quick_view denotes if this configuration is being loaded in a quick view modal.
     * @var {boolean} reconfig denotes if this configuration is updating an existing configuration.
     * @var {Array<string>} recommended_tabs is an array of the tabse that could have recommended accessories.
     * @var {[key: string]: any} elements is a collection of elements.
     * @var {boolean} can_upload_artwork flag if this configuration can upload artwork.
     * @var {boolean} artwork_required flag to trak if Artwork is required for this product.
     * @var {boolean} added_to_cart flag if this configuration has been added to the cart.
     * @var {boolean} update_designs flag if this configuration needs to update designs.
     * @var {boolean} purge_artwork flag if this configuration needs to purge artwork.
     * @var {boolean} update_quantity_of_designs flag if this configuration needs to update the quantity of designs.
     * @var {boolean} show_hide_toggle flag if all accessories have been shown.
     * @var {boolean} multiple_artwork_update_in_progress if user has begun a multiple design update
     * @var {string} modal_suffix of the current configuration.
     * @var {string} modal_style is the CSS style of the modal to keep it from moving.
     * @var {HTMLElement|null} modal_element the modal the reconfig in cart is in.
     * @var {ImageValidation} validator is the Image Validator for this configuration.
     * @var {boolean} config_in_progress flag to indicate a configuration is loading.
     * @var {Array<Object>} queue is an array of Selections made for the current configuration.
     * @var {NodeJS.Timer|null} timeout is a timer for the keep alive.
     * @var {NodeJS.Timer|null} throttle Throttle user inputs so that if they are rapidly making changes to a control we don't send ajax too soon.
     * @var {string} smart_code is the Smart Code of the configuration.
     * @var {string} configuration_type is the configuration type such as configurable or hardware.
     * @var {HTMLCollectionOf<HTMLInputElement>} quantity is a collection of Inputs identified as Order Quantities.
     * @var {PriceUpdater} priceUpdater is used to update the price shown above the configuration.
     * @var {Tabs} tabs is used to bind and manage the tab visibility, paging, and buttons in the config ui.
     * @var {StockStatus} stockStatus is used to update the displayed stock stats in the product heading.
     * @var {ConfigArtworkUploader} artworkUploader is used to initialize a set of artwork uploaders for the configuration.
     * @var {HTMLInputElement} selected_wall_input is the selected wall input for tent walls.
     * @var {boolean} suggest_tshirt_quantity is a flag to automatically adjust recommended shirt sizes to quantity and vice versa.
     * @var {boolean} skip_add_to_cart_modal is a flag that determines whether we're skipping the added to cart modal
     * @var {StickyCTA} sticky_cta is used to display the sticky call-to-action on the product page.
     */
    quick_view: boolean;
    reconfig: boolean;
    recommended_tabs: Array<string> = ['Cases__Bases', 'Accessories', 'Cases', 'Stakes__Weights'];
    elements: { [key: string]: any } = {};
    can_upload_artwork: boolean = false;
    artwork_required: boolean = false;
    added_to_cart: boolean = false;
    update_designs: boolean = false;
    purge_artwork: boolean = false;
    update_quantity_of_designs: boolean = false;
    show_hide_toggle: boolean = false;
    multiple_artwork_update_in_progress: boolean = false;
    modal_suffix: string;
    modal_style: string = '';
    modal_element: HTMLElement | null = null;
    validator: ImageValidation;
    config_in_progress: boolean = false;
    queue: Array<Object> = [];
    timeout: NodeJS.Timer | null = null;
    throttle: NodeJS.Timer | null = null;
    smart_code: string;
    configuration_type: string;
    quantity: HTMLCollectionOf<HTMLInputElement>;
    priceUpdater: PriceUpdater;
    tabs: Tabs;
    stockStatus: StockStatus;
    artworkUploader: ConfigArtworkUploader;
    selected_wall_input: HTMLInputElement;
    suggest_tshirt_quantity: boolean = false;
    quantity_input: HTMLCollectionOf<Element>;
    skip_add_to_cart_modal: boolean = true;
    sticky_cta: StickyCTA;
    yard_letter_preview: YardLetterPreview;

    /**
     * Binds to the elements on the page and starts the keep alive timeout interval.
     *
     * @param {boolean} quick_view denotes if this configuration is being loaded in a quick view modal.
     * @param {boolean} reconfig denotes if this configuration is updating an existing configuration.
     */
    constructor(quick_view, reconfig) {
        this.quick_view = quick_view || false;
        this.reconfig = reconfig || false;

        this.modal_suffix = this.quick_view ? '-modal' : '';
        this.initialize();

    }

    /**
     * Function for logic that needs to run when a new configuration is first loaded.
     *
     * @returns {void}
     */
    initialize(): void {
        this.validator = new ImageValidation(this.update_number_of_designs.bind(this), this.reconfig);
        this.smart_code = (
            <HTMLInputElement>document.getElementById('js-configuration-smart-code' + this.modal_suffix)
        ).value;
        this.configuration_type = (
            <HTMLInputElement>document.getElementById('configuration_type' + this.modal_suffix)
        ).value;
        this.quantity = <HTMLCollectionOf<HTMLInputElement>>document.getElementsByClassName('js-order-quantity');
        this.elements.color_picker_container = document.getElementsByClassName('tv-select__advanced-options-container');

        this.bind();
        this.calculate_free_shipping();
        this.set_timeout();

        // Initialize T-shirt Totals.
        if (this.elements.t_shirt_suggestions) {
            this.update_t_shirt_quantities();
        }

        this.priceUpdater = new PriceUpdater(
            this.reconfig ? this.elements.session_id.value : this.elements.product_id.value,
            this.quick_view,
            this.reconfig
        );

        // If the starting at quantity is greater than 1 we want to display the price per piece on page load.
        if (this.quantity.length > 0 && parseInt(this.quantity[0].value, 10) > 1) {
            this.priceUpdater.update(
                JSON.parse(this.elements.price_data.value)
            );
        }

        if (this.can_upload_artwork) {
            // Init the artwork uploader if this product uses artwork.
            this.initialize_artwork_uploader(true);

            // Download Artwork template not implemented for reconfigure
            if (!this.reconfig && this.elements.js_download_artwork_template) {
                // Download Artwork template link made visible.
                const download_template_url = window.location.origin +
                    '/index.php?dispatch=tv_artwork.get_template&smart_code='
                    + this.smart_code + '&branded=1&mode=download';
                this.elements.js_download_artwork_template.classList.remove('tv-display__none');
                this.elements.js_download_artwork_template.setAttribute('href', download_template_url);
            }
        }

        this.number_yard_letters();

        if (!this.reconfig) {
            this.sticky_cta = new StickyCTA();
        }
        if (this.elements.yard_shape_add_buttons || this.elements.color_picker_container) {
            this.handle_yard_letter_modals();
            if (this.elements.yard_shape_add_buttons && !this.reconfig) {
                this.yard_letter_preview = new YardLetterPreview();
            }
        }
        // Properly apply yard letter handler for edit-in-cart modals.
        $.ceEvent('on', 'ce.dialogshow', (function(data) {
            if (
                data[0].id === 'tv-yard-letter-message-modal' ||
                data[0].id === 'tv-yard-letter-character-modal' ||
                data[0].id === 'tv-yard-letter-color-modal'
            ) {
                // Exit early if on a product page
                if (!this.reconfig) {
                    return;
                }
                this.handle_yard_letter_modals();
            }
        }).bind(this));

        this.set_stock_status();
        // Only run video thumbnail script if there are video tabs
        const tv_tabs = Array.from(<HTMLCollectionOf<HTMLElement>>document.getElementsByClassName('ty-tabs__item'));
        const tv_mobile_tabs = Array.from(
            <HTMLCollectionOf<HTMLElement>>document.getElementsByClassName('ui-accordion-header')
        );
        tv_tabs.forEach(tv_tab => {
            if (tv_tab.innerText == "Videos") {
                this.create_video_thumbnail();
            }
        });
        tv_mobile_tabs.forEach(tv_mobile_tab => {
            if (tv_mobile_tab.innerText == "Videos") {
                this.create_video_thumbnail();
            }
        });

        // Calculate the fastest delivery time.
        this.initial_calculate_fastest_delivery();
    }

    /**
     * Binds the UI elements to the listeners.
     *
     * @private
     * @returns {void}
     */
    bind(): void {
        this.bind_to_inputs();
        this.bind_to_tabs();
        this.bind_to_tent_wall_inputs();
        this.bind_to_yard_yard_shape_inputs();
    }

    /**
     * Binds the input fields to the event listeners for keypress, clicks, and changes.
     *
     * @private
     * @returns {void}
     */
    bind_to_inputs(): void {
        // Configurator meta data.
        this.elements.session_id = document.getElementById('session_id' + this.modal_suffix);
        if (this.reconfig) {
            this.elements.original_session_id = document.getElementById('original_session_id');
        }
        this.elements.product_id = document.getElementById('product_id' + this.modal_suffix);
        this.elements.header_id = document.getElementById('header_id' + this.modal_suffix);
        this.elements.detail_id = document.getElementById('detail_id' + this.modal_suffix);

        // Navigation buttons.
        this.elements.prev = document.getElementsByClassName('js-tab-nav-button tv-button__prev');
        this.elements.next = document.getElementsByClassName('js-tab-nav-button tv-button__next');
        this.elements.links = document.getElementsByClassName('js-tablink');
        this.elements.config_top = document.getElementById('config-top');
        this.elements.top_links = document.querySelectorAll('a[href="#config-top"]');

        // By default, links to HTML elements are appended to the URL when clicked.
        // We want to stop this behavior and use JS to handle scrolling.
        this.elements.top_links.forEach(link => {
            link.addEventListener('click', e => {
                e.preventDefault();
                e.stopPropagation();
            });
        });

        // Special product elements.
        this.elements.t_shirt_suggestions = document.getElementsByClassName('js-tv-popular-t-shirt-sizes')[0];
        this.elements.custom_yard_sign_message = <HTMLTextAreaElement>document.getElementsByClassName(
            'js-tv-custom-message-yard-sign'
        )[0];
        this.elements.custom_yard_sign_shapes = <NodeListOf<HTMLInputElement>>document.querySelectorAll(
            '.tv-custom-message-yard-sign__input'
        );

        if (this.reconfig) {
            // Reconfiguration Save Button.
            this.elements.configurator_add_to_cart_buttons = document.querySelectorAll('#save_reconfiguration');
            this.elements.configurator_add_to_cart_buttons[0].addEventListener('click', () => {
                // Provide a warning to the user about possibly deleting artwork.
                if (this.purge_artwork) {
                    (() => new ConfirmDialog(
                        () => {
                            if (this.validator.order_quantity > 1) {
                                this.validator.reset_form()
                            } else {
                                this.save_reconfig();
                            }
                        },
                        null,
                        'tv_cart_artwork_delete_warning',
                        true
                    ))();
                } else {
                    this.save_reconfig();
                }
            });
        } else {
            // Add to cart buttons.
            this.elements.configurator_add_to_cart_buttons = document.querySelectorAll('.js-configurator-add-to-cart');
            for (const add_to_cart of this.elements.configurator_add_to_cart_buttons) {
                add_to_cart.addEventListener('click', this.hide_config_ui_and_show_summary.bind(this), false);
            }
        }
        this.elements.configurator_summary_modal_add_to_cart = document.querySelector(
            '.js-configurator-summary-add-to-cart'
        );

        // Load Most Popular Sizing button.
        if (this.elements.t_shirt_suggestions) {
            this.elements.t_shirt_suggestions.addEventListener(
                'click', () => {
                    this.queue_config(this.update_suggested_quantities());
                }
            );
        }

        // Request a quote buttons.
        this.elements.configurator_request_a_quote = document.querySelector('.js-configurator-request-a-quote');
        this.elements.configurator_summary_request_a_quote = document.querySelector(
            '.js-configurator-summary-request-a-quote'
        );

        // Download Artwork template link.
        this.elements.js_download_artwork_template = document.getElementById('js-download-artwork-template-link');

        // Click the configurator add to cart button when clicking the summary add to cart button.
        if (this.elements.configurator_summary_modal_add_to_cart) {
            this.elements.configurator_summary_modal_add_to_cart.addEventListener(
                'click', () => {
                    this.elements.configurator_add_to_cart_buttons[0].click();
                }
            );
        }

        // Click the configurator request a quote button when clicking the summary request a quote button.
        if (this.elements.configurator_summary_request_a_quote) {
            this.elements.configurator_summary_request_a_quote.addEventListener(
                'click', () => {
                    $.ceDialog('get_last').ceDialog('close');
                    this.elements.configurator_request_a_quote.click();
                }
            );
        }

        // Step number buttons.
        this.elements.step_number_buttons = document.getElementsByClassName('js-step-number-input');

        for (const step_number_button of this.elements.step_number_buttons) {
            step_number_button.addEventListener('click', this.update_number_input.bind(this), false);
        }

        // Advanced select dropdowns.
        this.elements.advanced_select_dropdowns = document.getElementsByClassName('tv-select__advanced');
        for (const advanced_select_dropdown of this.elements.advanced_select_dropdowns) {
            (() => new AdvancedSelect(advanced_select_dropdown, this.update.bind(this)))();
        }

        // Bind the mobile links to this.buttons.
        this.elements.mobile_links = document.getElementsByClassName('js-configurator-mobile-tablink');

        // Native product page price.
        this.elements.price_data = document.getElementById('js-config-price-data' + this.modal_suffix);
        const div_id = 'js-configurator-ui' + this.modal_suffix;

        this.elements.main_ui = document.getElementById(div_id);
        this.elements.main_ui.addEventListener('keypress', this.keypress.bind(this));

        this.elements.ui_options = document.getElementById('js-configurator-ui-options');

        this.elements.config_summary = document.getElementById(
            `js_config_summary_after_add_to_cart${this.modal_suffix}`
        );

        // Display config summary when add to wish list.
        this.elements.wish_list_button = document.getElementById(`button_wishlist_${this.elements.product_id.value}`);

        if (this.elements.wish_list_button) {
            this.elements.wish_list_button.addEventListener('click', this.hide_config_ui_and_show_summary.bind(this));
        }

        if (document.getElementById('js-configurator-reload-config')) {
            document.getElementById('js-configurator-reload-config')
                .addEventListener('click', this.start_new_config.bind(this));
        }

        // Advanced buttons at bottom of configurator
        this.elements.advanced_buttons = document.querySelector('.advanced-buttons');

        // Delivery estimator button
        this.elements.delivery_estimator_button = document.getElementById('js-delivery-estimator');

        // Request a quote.
        if (document.getElementById('js-request-a-quote')) {
            // Buttons.
            this.elements.request_a_quote = document.getElementById('js-request-a-quote');

            // Header and Footer.
            this.elements.pricing_heading = document.getElementById('js-product-details-heading');
            this.elements.product_footer = document.getElementById('js-advanced-product-footer');

            // Quote div.
            this.elements.quote_window = document.getElementById('js-product-quote-window');

            // Add listeners to buttons.
            this.elements.request_a_quote.addEventListener('click', this.request_a_quote.bind(this));
        }

        // Design online link.
        this.elements.design_tool_link = document.getElementById('js-design-tool-config-link');

        if (this.configuration_type === 'configurable') {
            const quantity_designs_modal = document.getElementById('js-quantity-designs-modal');
            if (quantity_designs_modal) {
                quantity_designs_modal.addEventListener('click', this.handle_design_online_click.bind(this));
            }
        }


        // Duplicate IDs for this have been removed and only one is needed here since all values are the same.
        this.elements.artwork_extensions = document.getElementsByClassName('js-cart-uploader-artwork-extensions')[0];

        // Input elements.
        this.elements.inputs = document.querySelectorAll(`#${div_id} .js-ui-form-field`);

        for (const input of this.elements.inputs) {
            input.addEventListener('change', this.event_update.bind(this), false);
            input.addEventListener('focusout', this.validate_form.bind(this), false);

            // Select input texts on click.
            if (input.type === 'text' || input.type === 'number') {
                input.addEventListener('click', e => { e.target.select(); }, false);
            }
        }

        // Check to see if the artwork upload modal is present to let us know if we are able to upload artwork.
        this.can_upload_artwork = document.getElementById(
            `js-configurator-artwork-upload-modal-${this.elements.session_id.value}`
        ) !== null;

        // Check to see if the Artwork/Graphics Tab is present to let us know Artwork is Required.
        this.artwork_required = document.getElementsByClassName('tv-configurator__tab-artwork-section').length > 0;

        // Free shipping check.
        this.elements.free_shipping_indicator = document.getElementById('js-free-shipping-indicator');
        this.elements.free_shipping_indicator.classList.add('hidden');
        this.elements.free_shipping_label = document.getElementById('js-free-shipping-label');

        if (this.elements.free_shipping_label) {
            this.elements.free_shipping_label.classList.add('tv-display__none');
        }

        this.elements.show_hide_accessories = document.getElementsByClassName('js-show-hide-accessories-tab-id');
        this.elements.show_buttons = {};
        this.elements.hide_buttons = {};
        for (const tab of this.elements.show_hide_accessories) {
            const tab_id = tab.value;
            this.elements.show_buttons[tab_id] = document.getElementById(
                'js_show_all_accessories_' + tab_id
            );
            if (this.elements.show_buttons[tab_id]) {
                const not_recommended = document.getElementsByClassName('not_recommended_' + tab_id);
                if (not_recommended.length === 0) {
                    this.elements.show_buttons[tab_id].classList.add('hidden');
                }

                this.elements.show_buttons[tab_id].addEventListener(
                    'click', this.show_all_accessories.bind(this, tab_id)
                );
            }

            this.elements.hide_buttons[tab_id] = document.getElementById(
                'js_only_recommended_accessories_' + tab_id
            );
            if (this.elements.hide_buttons[tab_id]) {
                this.elements.hide_buttons[tab_id].addEventListener(
                    'click', this.only_recommended_accessories.bind(this, tab_id)
                );
            }
        }

        if (!this.reconfig) {
            // Use post hook to redirect to Added to Cart page after AJAX response comes back.
            $.ceEvent('on', `ce.formajaxpost_product_form_${this.elements.product_id.value}`, (response) => {
                // Only proceed if no notifications were sent back.
                if (Object.keys(response.notifications).length === 0) {
                    setTimeout(() => {
                        $.toggleStatusBox('show', {
                            overlay: true,
                            statusContent: `
                                <span class="ty-ajax-loading-box-with__text-wrapper">
                                    Adding product to cart
                                </span>
                            `,
                            statusClass: 'ty-ajax-loading-box_text_block'
                        });
                        // Only redirect to 'added to cart' page if this is not a quote request
                        const reload_config_btn = document.getElementById('js-reload-config');
                        if (reload_config_btn) {
                            $.toggleStatusBox('hide');
                            reload_config_btn.addEventListener('click', () => {
                                location.reload();
                            });
                        } else {
                            window.location.replace(`${window.location.origin}/index.php?dispatch=tv_cart.added_to_cart`);
                        }
                    }, 100);

                }
            });
        }

    }

    handle_design_online_click(event: Event) {
        const element = <HTMLElement>event.target;
        if (element && element.id === 'js-design-tool-config-link' ||
            element.parentElement.id === 'js-design-tool-config-link'
        ) {
            this.design_tool_warning();
        }
    }

    /**
     * Handles the yard letter Message, Character, and Color modals
     *
     * @returns {void}
     */
    handle_yard_letter_modals(): void {
        let js_yard_letter_word_labels = <HTMLCollectionOf<HTMLElement>>document.getElementsByClassName(
            'js-yard-letter-word-label'
        );
        if (js_yard_letter_word_labels) {
            let js_yard_letter_height = [];
            // Find the height of all yard letter labels
            for (const label of js_yard_letter_word_labels) {
                js_yard_letter_height.push(label.offsetHeight);
            }

            // Make all label heights equal to the largest height
            for (const label of js_yard_letter_word_labels) {
                label.style.height = Math.max(...js_yard_letter_height).toString() + "px";
            }
        }

        const toc_titles = document.getElementsByClassName('tv-yard-toc-title');
        const toc_headers = document.getElementsByClassName('tv-yard-header');
        for (let i = 0; i < toc_titles.length; i++) {
            toc_titles[i].addEventListener('click', this.bind_yard_letter_modal_element.bind(
                this,
                toc_titles,
                toc_titles[i],
                toc_headers[i],
                'tv-yard-char-toc-selected'
            ));
        }

        // Grab the correct list of swatches
        let yard_letter_inputs = document.getElementsByClassName('tv-select__advanced');
        let current_swatches;
        const modal_swatches = <NodeListOf<HTMLElement>>document.querySelectorAll('.tv-modal-swatch');
        let current_window_position;
        let window_scroll_flag;
        Array.from(yard_letter_inputs).forEach(yard_letter_input => {
            yard_letter_input.addEventListener('click', () => {
                // Removed selected class from all inputs
                for (let yard_letter_input of yard_letter_inputs) {
                    yard_letter_input.classList.remove('js-current-swatch-selector');
                }
                // Add selected class to chosen input
                yard_letter_input.classList.add('js-current-swatch-selector');
                current_swatches = document.getElementsByClassName('js-current-swatch-selector')[0].querySelectorAll(
                    '.tv-color-picker__select-option, .tv-color-picker-yard-shape__select-option'
                );

                // Grab the background style of the currently selected swatch
                let current_swatch_bg_img = (<HTMLElement>yard_letter_input.querySelector(
                    '.tv-color-picker__swatch, .tv-color-picker-yard-shape__swatch'
                )).style.backgroundImage;
                let current_swatch_bg_color = (<HTMLElement>yard_letter_input.querySelector(
                    '.tv-color-picker__swatch, .tv-color-picker-yard-shape__swatch'
                )).style.backgroundColor;

                // Apply the selected class to the modal swatch based on what is currently selected
                if (current_swatch_bg_img && current_swatch_bg_img != "initial") {
                    for (const modal_swatch of modal_swatches) {
                        if (modal_swatch.style.backgroundImage == current_swatch_bg_img) {
                            modal_swatch.parentElement.classList.add('js-yard-color-swatch__selected');
                        } else {
                            modal_swatch.parentElement.classList.remove('js-yard-color-swatch__selected');
                        }
                    }
                } else if (current_swatch_bg_color) {
                    for (const modal_swatch of modal_swatches) {
                        if (modal_swatch.style.backgroundColor == current_swatch_bg_color) {
                            modal_swatch.parentElement.classList.add('js-yard-color-swatch__selected');
                        } else {
                            modal_swatch.parentElement.classList.remove('js-yard-color-swatch__selected');
                        }
                    }
                }
                current_swatches = document.getElementsByClassName('js-current-swatch-selector')[0].querySelectorAll(
                    '.tv-color-picker__select-option, .tv-color-picker-yard-shape__select-option'
                );
                current_window_position = window.scrollY;
                window_scroll_flag = true;
            });
        });

        $(window).scroll(() => {
            // Maintain window position and adjust the modal position accordingly when the user opens a modal on mobile
            if (window_scroll_flag && window.innerWidth < 767) {
                const dialog = $.ceDialog('get_last')[0].parentElement;
                if (dialog) {
                    dialog.style.top = current_window_position + 10 + "px";
                }
                scrollTo(0, current_window_position);
                window_scroll_flag = false;
            }
        });

        // Yard Letter Modal Save Buttons
        const js_yard_letters_message_save_button = document.getElementById('js-yard-letters__message-save-button');
        const js_yard_letters_character_save_button = document.getElementById('js-yard-letters__character-save-button');
        const js_yard_letters_color_save_button = document.getElementById('js-yard-letters__color-save-button');
        const js_yard_color_swatches = document.getElementsByClassName('js-yard-color-swatch');

        for (const js_yard_color_swatch of js_yard_color_swatches) {
            js_yard_color_swatch.addEventListener(
                'click',
                this.bind_yard_letter_swatches.bind(
                    this,
                    js_yard_color_swatches,
                    js_yard_color_swatch,
                    js_yard_letters_message_save_button,
                    js_yard_letters_character_save_button,
                    js_yard_letters_color_save_button,
                    'js-yard-color-swatch__selected'
                )
            )
        }

        // Functionality for message swatch submit button
        if (js_yard_letters_message_save_button) {
            js_yard_letters_message_save_button.addEventListener('click', () => {
                let selected_color = <HTMLElement>document.getElementsByClassName(
                    'js-yard-color-swatch__selected'
                )[0].childNodes[1];
                current_swatches = document.getElementsByClassName('js-current-swatch-selector')[0].querySelectorAll(
                    '.tv-color-picker__select-option, .tv-color-picker-yard-shape__select-option'
                );
                // Find the input color swatch that matches the modal swatch selected, and click it.
                if (selected_color.style.backgroundColor) {
                    current_swatches.forEach(current_swatch => {
                        if (selected_color.style.backgroundColor == current_swatch.querySelectorAll(
                                '.tv-color-picker__swatch'
                            )[0].style.backgroundColor
                        ) {
                            current_swatch.click();
                        }
                    });
                // Find the input image swatch that matches the modal swatch selected, and click it.
                } else {
                    current_swatches.forEach(current_swatch => {
                        if (selected_color.style.backgroundImage == current_swatch.querySelectorAll(
                                '.tv-color-picker__swatch'
                            )[0].style.backgroundImage
                        ) {
                            current_swatch.click();
                        }
                    });
                }
                // Force the modal to close
                $.ceDialog('get_last').ceDialog('close');
            });
        }

        // Functionality for character swatch submit button
        if (js_yard_letters_character_save_button) {
            js_yard_letters_character_save_button.addEventListener('click', () => {
                let selected_color = <HTMLElement>document.getElementsByClassName(
                    'js-yard-color-swatch__selected'
                )[0].childNodes[1];
                current_swatches = document.getElementsByClassName('js-current-swatch-selector')[0].querySelectorAll(
                    '.tv-color-picker__select-option, .tv-color-picker-yard-shape__select-option'
                );
                // Find the input color swatch that matches the modal swatch selected, and click it.
                if (selected_color.style.backgroundColor) {
                    current_swatches.forEach(current_swatch => {
                        if (selected_color.style.backgroundColor == current_swatch.querySelectorAll(
                                '.tv-color-picker__swatch'
                            )[0].style.backgroundColor
                        ) {
                            current_swatch.click();
                        }
                    });
                    // Find the input image swatch that matches the modal swatch selected, and click it.
                } else {
                    current_swatches.forEach(current_swatch => {
                        if (selected_color.style.backgroundImage == current_swatch.querySelectorAll(
                                '.tv-color-picker__swatch'
                            )[0].style.backgroundImage
                        ) {
                            current_swatch.click();
                        }
                    });
                }
                // Force the modal to close
                $.ceDialog('get_last').ceDialog('close');
            });
        }

        // Functionality for color swatch submit button
        if (js_yard_letters_color_save_button) {
            js_yard_letters_color_save_button.addEventListener('click', () => {
                let selected_color = <HTMLElement>document.getElementsByClassName(
                    'js-yard-color-swatch__selected'
                )[0].childNodes[1];
                current_swatches = document.getElementsByClassName('js-current-swatch-selector')[0].querySelectorAll(
                    '.tv-color-picker__select-option, .tv-color-picker-yard-shape__select-option'
                );
                // Find the input color swatch that matches the modal swatch selected, and click it.
                if (selected_color.style.backgroundColor) {
                    current_swatches.forEach(current_swatch => {
                        if (selected_color.style.backgroundColor == current_swatch.querySelectorAll(
                                '.tv-color-picker-yard-shape__swatch'
                            )[0].style.backgroundColor
                        ) {
                            current_swatch.click();
                        }
                    });
                    // Find the input image swatch that matches the modal swatch selected, and click it.
                } else {
                    current_swatches.forEach(current_swatch => {
                        if (selected_color.style.backgroundImage == current_swatch.querySelectorAll(
                                '.tv-color-picker-yard-shape__swatch'
                            )[0].style.backgroundImage
                        ) {
                            current_swatch.click();
                        }
                    });
                }
                // Force the modal to close
                $.ceDialog('get_last').ceDialog('close');
            });
        }

        // Modal Close Buttons
        const yard_letter_close_buttons = document.getElementsByClassName('js-yard-letter-closer');
        for (const yard_letter_close_button of yard_letter_close_buttons) {
            yard_letter_close_button.addEventListener('click', () => {
                $.ceDialog('get_last').ceDialog('close');
            });
        }

        // When accessing the message input on mobile, scroll to its position so the digital keyboard doesn't cover it.
        if (this.elements.custom_yard_sign_message) {
            this.elements.custom_yard_sign_message.addEventListener('click', e => {
                const element = <HTMLElement>e.target;
                if (window.innerWidth < 767) {
                    const message_dist_from_top = window.pageYOffset + element.getBoundingClientRect().top - 50;
                    window.scrollTo(0, message_dist_from_top);
                }
            });
        }

    }

    /**
     * Handle the click events for the Yard Letter Color Swatches
     * and their respective 'selected' classes
     * @returns {void}
     */
    bind_yard_letter_swatches(
        swatches: HTMLCollection,
        current_swatch: HTMLElement,
        message_save_button: HTMLElement,
        character_save_button: HTMLElement,
        color_save_button: HTMLElement,
        selected_class: string
    ): void {
        for (const swatch of swatches) {
            swatch.classList.remove(selected_class);
        }
        current_swatch.classList.add(selected_class);

        if (current_swatch.classList.contains('message-swatch')) {
            message_save_button.classList.remove('tv-disabled__button');
        } else if (current_swatch.classList.contains('char-swatch')) {
            character_save_button.classList.remove('tv-disabled__button');
        } else if (current_swatch.classList.contains('color-swatch')) {
            color_save_button.classList.remove('tv-disabled__button');
        }

    }

    /**
     * Handle the click events for the Table of Contents
     * for the Yard Letter Modals.
     *
     * @returns {void}
     */
    bind_yard_letter_modal_element(
        titles: HTMLCollection,
        current_title: HTMLElement,
        header: HTMLElement,
        selected_class: string
    ): void {
        for (const title of titles) {
            title.classList.remove(selected_class);
        }
        current_title.classList.add(selected_class);
        const current_window_position = window.scrollY
        setTimeout(() => header.scrollIntoView(), 0);
        window.scrollTo(0, current_window_position);
    }

    /**
     * Disable all Configurator breaking buttons such as
     * Add to Cart, Design Links, and Tabs.
     *
     * @return {void}
     */
    disable_buttons(): void {
        // Hide the add to cart or save reconfig buttons.
        for (const button of this.elements.configurator_add_to_cart_buttons) {
            button.classList.add('tv-display__none');
        }
        // Also remove the Multiple Design link until it reloads.
        const design_links = <HTMLCollectionOf<HTMLElement>>document.getElementsByClassName(
            'js-update-quantity-designs-button'
        );
        if (design_links) {
            for (const link of design_links) {
                link.classList.add('tv-display__none');
            }
        }
    }

    /**
     * Enable buttons if nothing changed such as
     * Add to Cart, Design Links, and Tabs.
     *
     * @return {void}
     */
    enable_buttons(): void {
        // Show the Multiple Design link.
        const design_links = <HTMLCollectionOf<HTMLElement>>document.getElementsByClassName(
            'js-update-quantity-designs-button'
        );
        if (design_links) {
            for (const link of design_links) {
                link.classList.remove('tv-display__none');
            }
        }
    }

    /**
     * Method to call the Ajax and calculate Product Free Shipping.
     *
     * @returns {void}
     */
    calculate_free_shipping(): void {
        const price_data_json = this.elements.price_data.value;
        // Price Data has to be available and we should wait for the Configuration to finish.
        if (price_data_json !== '' && this.queue.length === 0) {
            const priceData = JSON.parse(price_data_json);
            if (!priceData) {
                return;
            }
            $.ceAjax('request', 'index.php?dispatch=tv_shipping.product_free_shipping_check', {
                result_ids: '',
                overlay: false,
                full_render: false,
                method: 'POST',
                caching: false,
                data: {
                    price: priceData.total_price,
                    product_id: parseInt(this.elements.product_id.value, 10),
                    configuration_id: this.elements.session_id.value
                },
                callback: results => {
                    const response = results.json;
                    // Check if there is a response before changing the indicator.
                    if (response) {
                        if (
                            response.free_shipping_promo ||
                            (response.price_group_free_shipping && !response.tv_non_free_shipping) ||
                            response.product_free_shipping
                        ) {
                            if (this.elements.free_shipping_indicator) {
                                this.elements.free_shipping_indicator.classList.remove('hidden');
                            }
                            if (this.elements.free_shipping_label) {
                                this.elements.free_shipping_label.classList.remove('tv-display__none');
                            }
                        } else {
                            if (this.elements.free_shipping_indicator) {
                                this.elements.free_shipping_indicator.classList.add('hidden');
                            }
                            if (this.elements.free_shipping_label) {
                                this.elements.free_shipping_label.classList.add('tv-display__none');
                            }
                        }
                    }
                }
            });
        }
    }

    /**
     * Event function for Show All Accessories button
     *
     * @param {string} tab_id the Tab we are working out of.
     * @returns {void}
     */
    show_all_accessories(tab_id): void {
        const not_recommended = document.getElementsByClassName('not_recommended_' + tab_id);
        if (not_recommended.length > 0) {
            for (const accessory of not_recommended) {
                accessory.classList.remove('tv-display__none');
            }
            this.elements.show_buttons[tab_id].classList.add('tv-display__none');
            this.show_hide_toggle = true;
        }
        const dialog = $.ceDialog('get_last')[0];
        if (dialog) {
            $.ceDialog('get_last').ceDialog('reload');
        }
    }

    /**
     * Creates numbering for Yard Letter Character Headings
     *
     * @returns {void}
     */
    number_yard_letters(): void {
        let js_yard_letter_heading_number = document.getElementsByClassName('js-yard-letter-heading-number');
        for (let i = 0; i < js_yard_letter_heading_number.length; i++) {
            let actual_number = i + 1;
            js_yard_letter_heading_number[i].innerHTML = " " + actual_number;
        }
    }

    /**
     * Event function for Only Recommended Accessories button
     *
     * @param {string} tab_id the Tab we are working out of.
     * @returns {void}
     */
    only_recommended_accessories(tab_id): void {
        const not_recommended = document.getElementsByClassName('not_recommended_' + tab_id);
        if (not_recommended.length > 0) {
            for (const accessory of not_recommended) {
                accessory.classList.add('tv-display__none');
            }
            this.elements.show_buttons[tab_id].classList.remove('tv-display__none');
            this.elements.hide_buttons[tab_id].classList.add('tv-display__none');
            this.show_hide_toggle = false;
        }
    }

    /**
     * Builds the data and makes the ajax request to save the configuration in the cart.
     *
     * @returns {void}
     */
    save_reconfig(): void {
        const data = {
            'original_session_id': this.elements.original_session_id.value,
            'product_id': this.elements.product_id.value,
            'session_id': this.elements.session_id.value,
            'header_id': this.elements.header_id.value,
            'detail_id': this.elements.detail_id.value,
            'purge_artwork': this.purge_artwork,
            'is_hardware': this.configuration_type === 'hardware',
            'amount': this.quantity[0]?.value
        };

        $.ceAjax('request', 'index.php?dispatch=checkout.update_reconfig', {
            'full_render': false,
            'method': 'POST',
            'caching': false,
            'data': data,
            'message': 'Updating Cart',
            'callback': () => {
                // Reload cart page clearing cache.
                window.location.assign('index.php?dispatch=checkout.cart');

            }
        });
    }

    /**
     * Hides the config ui element and shows the config summary in its place.
     * To be used when the config is added to cart, so they can't keep trying to change things.
     *
     * @param {event} event clicking on Add To Cart.
     *
     * @returns {void}
     */
    hide_config_ui_and_show_summary(event): void {
        // Validate custom message yard sign quantities.
        if (this.elements.custom_yard_sign_message && !this.validate_custom_yard_sign_message_add_to_cart()) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }

        // Validate t-shirt quantities are correct.
        if (this.elements.t_shirt_suggestions && !this.validate_t_shirt_quantities()) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }

        // Hide estimate delivery button and advanced buttons at bottom
        this.elements.advanced_buttons.classList.add('tv-display__none');

        // Hide the config options and show the config summary.
        this.elements.ui_options.classList.add('hidden');
        this.elements.config_summary.classList.remove('hidden');

        this.added_to_cart = true;

        // Clear the keep alive timeout.
        if (this.timeout) {
            clearInterval(this.timeout);
        }

        // Check if the selected Order Type is Hardware Only and hide the Summary Artwork items accordingly.
        if (this.tabs.is_hardware_only()) {
            const summary_templates = document.getElementById('js-config-summary-artwork-templates');
            if (summary_templates) {
                summary_templates.classList.add('tv-display__none');
            }

            const summary_artwork_btns = document.getElementById('js-config-summary-artwork-buttons');
            if (summary_artwork_btns) {
                summary_artwork_btns.classList.add('tv-display__none');
            }
        }

        // Exit early if this is a hardware item.
        if (this.configuration_type === 'hardware') {
            return;
        }

        // Hide the add to cart button in the config summary modal so you can't add to cart again.
        if (this.elements.configurator_summary_modal_add_to_cart) {
            this.elements.configurator_summary_modal_add_to_cart.classList.add('hidden');
            const modal_btn_cart = document.getElementById(`modal_button_cart_${this.elements.product_id.value}`);
            if (modal_btn_cart) {
                modal_btn_cart.classList.add('hidden');
            }
        }

        // Hide Add to Cart button in Upload Artwork Modal.
        const upload_add_to_cart = document.getElementById(
            `js-upload-artwork-add-to-cart-${this.elements.session_id.value}`
        );
        if (upload_add_to_cart) {
            upload_add_to_cart.classList.add('hidden');
        }

        // Show View Cart and Checkout buttons in Upload Artwork Modal.
        const view_cart_or_checkout = document.getElementById(
            `js-view-cart-or-checkout-${this.elements.session_id.value}`
        );
        if (view_cart_or_checkout) {
            view_cart_or_checkout.classList.remove('hidden');
        }

    }

    /**
     * Updates t-shirt size quantities based on the total quantity.
     *
     * @returns {any[]} selections of updated Shirt Size Quantities.
     */
    update_suggested_quantities(): any[] {
        this.elements.size_category = document.getElementsByClassName('tv_t-shirt__size_main_option')[0];
        const current_quantity = parseInt(this.quantity[0].value, 10);
        const selections = [];

        if (this.elements.size_category.value === 'Youth') {
            this.elements.youth_s = document.getElementsByClassName('tv-t-shirt__size-s')[0];
            this.elements.youth_m = document.getElementsByClassName('tv-t-shirt__size-m')[0];
            this.elements.youth_l = document.getElementsByClassName('tv-t-shirt__size-l')[0];

            // Set Defaults
            this.elements.youth_s.setAttribute('value', 0);
            this.elements.youth_m.setAttribute('value', 0);
            this.elements.youth_l.setAttribute('value', 0);
            this.elements.new_value_s_l = 0;

            // If 30% of the quantity is greater than 1.
            if (current_quantity > 3) {
                this.elements.new_value_s_l = Math.floor(current_quantity * 0.33);
                this.elements.youth_s.setAttribute('value', this.elements.new_value_s_l);
                this.elements.youth_l.setAttribute('value', this.elements.new_value_s_l);
            }

            // Throw the remainder into medium.
            const current_total = (this.elements.new_value_s_l * 2);
            this.elements.youth_m.setAttribute('value', current_quantity - current_total);

            // Collect the updated selections to Queue for Config at the final step.
            selections.push({
                'ID': this.elements.youth_s.getAttribute('name'),
                'Value': this.elements.youth_s.value
            }, {
                'ID': this.elements.youth_m.getAttribute('name'),
                'Value': this.elements.youth_m.value
            }, {
                'ID': this.elements.youth_l.getAttribute('name'),
                'Value': this.elements.youth_l.value
            });

            return this.update_t_shirt_quantities(selections);
        }

        // Otherwise we're working with the adult sizes.
        this.elements.adult_s = document.getElementsByClassName('tv-t-shirt__size-s')[0];
        this.elements.adult_m = document.getElementsByClassName('tv-t-shirt__size-m')[0];
        this.elements.adult_l = document.getElementsByClassName('tv-t-shirt__size-l')[0];
        this.elements.adult_xl = document.getElementsByClassName('tv-t-shirt__size-xl')[0];
        this.elements.adult_2xl = document.getElementsByClassName('tv-t-shirt__size-2xl')[0];
        this.elements.adult_3xl = document.getElementsByClassName('tv-t-shirt__size-3xl')[0];

        // Set Defaults
        this.elements.adult_s.setAttribute('value', 0);
        this.elements.adult_m.setAttribute('value', 0);
        this.elements.adult_l.setAttribute('value', 0);
        this.elements.adult_xl.setAttribute('value', 0);
        this.elements.adult_2xl.setAttribute('value', 0);
        this.elements.adult_3xl.setAttribute('value', 0);
        this.elements.new_value_s_2xl = 0;
        this.elements.new_value_m_xl = 0;

        // If 10% of the quantity is greater than 1.
        if (current_quantity > 10) {
            this.elements.new_value_s_2xl = Math.floor(current_quantity * 0.1);
            this.elements.adult_s.setAttribute('value', this.elements.new_value_s_2xl);
            this.elements.adult_2xl.setAttribute('value', this.elements.new_value_s_2xl);
        }

        // If 25% of the quantity is greater than 1.
        if (current_quantity > 4) {
            this.elements.new_value_m_xl = Math.floor(current_quantity * 0.25);
            this.elements.adult_m.setAttribute('value', this.elements.new_value_m_xl);
            this.elements.adult_xl.setAttribute('value', this.elements.new_value_m_xl);
        }

        // Throw the remainder into large.
        const current_total = (this.elements.new_value_m_xl * 2) + (this.elements.new_value_s_2xl * 2);
        this.elements.adult_l.setAttribute('value', current_quantity - current_total);

        // Collect the updated selections to Queue for Config at the final step.
        selections.push({
            'ID': this.elements.adult_s.getAttribute('name'),
            'Value': this.elements.adult_s.value
        }, {
            'ID': this.elements.adult_m.getAttribute('name'),
            'Value': this.elements.adult_m.value
        }, {
            'ID': this.elements.adult_l.getAttribute('name'),
            'Value': this.elements.adult_l.value
        }, {
            'ID': this.elements.adult_xl.getAttribute('name'),
            'Value': this.elements.adult_xl.value
        }, {
            'ID': this.elements.adult_2xl.getAttribute('name'),
            'Value': this.elements.adult_2xl.value
        }, {
            'ID': this.elements.adult_3xl.getAttribute('name'),
            'Value': this.elements.adult_3xl.value
        });

        return this.update_t_shirt_quantities(selections);
    }

    /**
     * Validate T-shirt quantities.
     *
     * @returns {boolean} with validation status.
     */
    validate_t_shirt_quantities(): boolean {
        this.init_t_shirt_values();

        // Validate they haven't entered a wrong quantity.
        if (this.elements.total_quantity <= 0) {
            Notifications.set_notification(
                'W',
                'Warning:',
                'You do not have enough shirt sizes selected, you will not be able to add to the cart.'
            );

            return false;
        }

        return true;
    }

    /**
     * Validate Custom Yard Sign Messages total characters before adding to the cart.
     *
     * @returns {boolean}
     */
    validate_custom_yard_sign_message_add_to_cart(): boolean {
        let emojiCount = 0;

        for (let i = 0; i < this.elements.custom_yard_sign_shapes.length; i++) {
            emojiCount += parseInt(this.elements.custom_yard_sign_shapes[i].value);
        }

        if (this.elements.custom_yard_sign_message.value.length + emojiCount < 4) {
            Notifications.set_notification(
                'W',
                'Warning:',
                'You must enter a total of 4 yard sign shapes, letters, or a combination of both before being able to add this product to the cart.'
            );

            return false;
        }

        return true;
    }

    /**
     * Validate Custom Yard Sign Messages.
     *
     * @param {HTMLElement} input that's going to be validated.
     *
     * @returns {boolean}
     *
     */
    validate_custom_yard_sign_message(input): boolean {
        let emojiCount = 0;
        let emojiValueBlank = false

        for (let i = 0; i < this.elements.custom_yard_sign_shapes.length; i++) {
            emojiCount += parseInt(this.elements.custom_yard_sign_shapes[i].value);

            if (this.elements.custom_yard_sign_shapes[i].value === "") {
                emojiValueBlank = true;
            }
        }

        const regExpr = new RegExp(/^[a-zA-Z\d\,\?\!\#\$\%\&\'\s]+$/g);
        const evaluateRegExpr = regExpr.test(this.elements.custom_yard_sign_message.value);

        input.setCustomValidity('');

        if (emojiValueBlank === true) {
            input.setCustomValidity("Yard sign shape quantities may not be blank");
            return false;
        }

        if (this.elements.custom_yard_sign_message.value === "" && emojiCount === 0) {
            input.setCustomValidity("Message must be filled in or a quantity must be selected for a yard sign shape");
            return false;
        }

        if (this.elements.custom_yard_sign_message.value.length > 100) {
            input.setCustomValidity("Message must be 100 characters or less");
            return false;
        }

        if (this.elements.custom_yard_sign_message.value != "") {
            if (!evaluateRegExpr) {
                input.setCustomValidity("Message may only include alphanumeric characters and ? ! , '  # $ % &");
                return false;
            }
        }

        // Convert the message to uppercase.
        let convert_to_uppercase = this.elements.custom_yard_sign_message.value.toUpperCase();
        this.elements.custom_yard_sign_message.value = convert_to_uppercase;

        return true;
    }

    /**
     * Initialize T-Shirt values
     *
     * @returns {void}
     */
    init_t_shirt_values(): void {
        this.elements.size_category = document.getElementsByClassName('tv_t-shirt__size_main_option')[0];

        if (this.elements.size_category.value === 'Youth') {
            this.elements.youth_s = document.getElementsByClassName('tv-t-shirt__size-s')[0];
            this.elements.youth_m = document.getElementsByClassName('tv-t-shirt__size-m')[0];
            this.elements.youth_l = document.getElementsByClassName('tv-t-shirt__size-l')[0];

            this.elements.total_quantity = parseInt(this.elements.youth_s.value, 10)
                + parseInt(this.elements.youth_m.value, 10) + parseInt(this.elements.youth_l.value, 10);

            return;
        }

        // Otherwise we're working with the adult sizes.
        this.elements.adult_s = document.getElementsByClassName('tv-t-shirt__size-s')[0];
        this.elements.adult_m = document.getElementsByClassName('tv-t-shirt__size-m')[0];
        this.elements.adult_l = document.getElementsByClassName('tv-t-shirt__size-l')[0];
        this.elements.adult_xl = document.getElementsByClassName('tv-t-shirt__size-xl')[0];
        this.elements.adult_2xl = document.getElementsByClassName('tv-t-shirt__size-2xl')[0];
        this.elements.adult_3xl = document.getElementsByClassName('tv-t-shirt__size-3xl')[0];

        this.elements.total_quantity = parseInt(this.elements.adult_s.value, 10)
            + parseInt(this.elements.adult_m.value, 10) + parseInt(this.elements.adult_l.value, 10)
            + parseInt(this.elements.adult_xl.value, 10) + parseInt(this.elements.adult_2xl.value, 10)
            + parseInt(this.elements.adult_3xl.value, 10);
    }

    /**
     * Update t-shirt remaining and total quantities
     *
     * @param {array} selections selections to Queue for Configuration.
     * @returns {any[]} selections of updated T-Shirt Product Quantity.
     */
    update_t_shirt_quantities(selections = []): any[] {
        this.init_t_shirt_values();

        // Update Total Number of Shirts to keep price per piece accurate.
        if (parseInt(this.quantity[0].value, 10) !== this.elements.total_quantity) {
            selections.push({
                'ID': this.quantity[0].getAttribute('name'),
                'Value': this.elements.total_quantity
            });
        }

        return selections;
    }

    /**
     * Starts a new configuration so customer can do multiple configurations for the same product.
     *
     * @returns {void}
     */
    start_new_config(): void {
        $.ceAjax('request', 'index.php?dispatch=tv_configurator.init', {
            'result_ids': 'js-configurator-ui' + this.modal_suffix,
            'full_render': true,
            'method': 'POST',
            'caching': false,
            'data': {
                product_id: this.elements.product_id.value
            },
            'message': 'Initializing Product Configuration',
            'callback': () => {
                (() => new TVModal())();
                this.tabs = null;
                this.reconfig = false;
                this.initialize();
                this.priceUpdater.update(JSON.parse(this.elements.price_data.value));

                // Display advanced buttons at bottom of configurator
                if (this.elements.advanced_buttons) {
                    this.elements.advanced_buttons.classList.remove('tv-display__none');
                }
            }
        });
    }

    /**
     * Bind yard shape inputs if the message element exists.
     *
     * @returns {void}
     */
    bind_to_yard_yard_shape_inputs(): void {
        if (this.elements.custom_yard_sign_message) {
            this.elements.yard_shape_remove_buttons = document.getElementsByClassName('js-yard-shape-remove');
            this.elements.yard_shape_add_buttons = document.getElementById('js-add-yard-shape');

            for (const remove_buttons of this.elements.yard_shape_remove_buttons) {
                remove_buttons.addEventListener('click', this.remove_shape_selection.bind(this), false);
            }

            if (this.elements.yard_shape_add_buttons) {
                this.elements.yard_shape_add_buttons.addEventListener('click', this.add_shape_selection.bind(this));
            }
        }
    }

    /**
     * Bind the tent wall inputs.
     *
     * @returns {void}
     */
    bind_to_tent_wall_inputs(): void {
        // Tent wall modal elements.
        this.elements.tent_walls_options = document.getElementsByClassName('tv-tent__walls-selection-container');
        this.elements.tent_walls_selected = document.querySelectorAll('input[name="wall_selections"]:checked');
        this.elements.tent_walls_selections = document.getElementsByClassName('tv-tent__walls-selection');
        this.elements.tent_walls_save_buttons = document.getElementsByClassName('tv-tent__walls-save-button');
        this.elements.tent_walls_remove_buttons = document.getElementsByClassName(
            'tv-configurator__remove-wall-selection'
        );
        this.elements.tent_walls_remove_inputs = document.getElementsByClassName('js-tent__walls-remove-input');

        // Bind the tent wall inputs.
        for (const tent_wall_option of this.elements.tent_walls_options) {
            tent_wall_option.querySelector('input').addEventListener('click', this.select_wall_input.bind(this), false);
        }

        // Bind the save wall buttons.
        for (const save_wall_button of this.elements.tent_walls_save_buttons) {
            save_wall_button.addEventListener('click', this.save_wall_selection.bind(this), false);
        }

        // If there are no selections made and the product is wall only, then choose the first option.
        if (this.elements.tent_walls_selections.length > 0 && this.elements.tent_walls_selected.length === 0) {
            if (this.elements.tent_walls_selections[0].dataset.wallOnly === 'true') {
                const selection = [{
                    'ID': this.elements.tent_walls_selections[0].dataset.id,
                    'Value': this.elements.tent_walls_selections[0].value
                }];
                this.queue_config(selection);
            }
        }

        // Hide the remove button if there is only one selection and the product is wall only.
        if (this.elements.tent_walls_selected.length >= 1) {
            for (const tent_walls_remove_buttons of this.elements.tent_walls_remove_buttons) {
                tent_walls_remove_buttons.style.display = 'inline-block';
                if (this.elements.tent_walls_remove_buttons.length === 1 &&
                    this.elements.tent_walls_selections[0].dataset.wallOnly === 'true'
                ) {
                    tent_walls_remove_buttons.style.display = 'none';
                }
            }
        }
    }

    /**
     * Calculate the fastest delivery date on initialization.
     *
     * @returns {void}
     */
    initial_calculate_fastest_delivery(): void {
        // Set the production times.
        const production_times = JSON.parse((<HTMLInputElement>document.querySelector('.js-production-times')).value);

        $.ceAjax('request', 'index.php?dispatch=tv_configurator.initial_calculate_fastest_delivery', {
            method: 'POST',
            cache: false,
            hidden: true,
            data: {
                production_times: production_times
            },
            result_ids: 'js-fastest-delivery',
            message: 'Calculating Fastest Delivery',
            callback: () => {
                if (!this.reconfig) {
                    // Delivery Estimator Button Unhidden
                    document.getElementById('js-shipping-estimator-opener-link').classList.remove('tv-display__none');
                    if (this.elements.delivery_estimator_button) {
                        this.elements.delivery_estimator_button.classList.remove('tv-display__none');
                    }
                }
            }
        });
    }

    /** TODO CSCART4 - Maybe make this into a generic function and accept a message and action. */
    /**
     * Display a confirm alert that warns the user they are navigating away.
     *
     * @returns {void}
     */
    design_tool_warning(): void {
        const product_id = this.elements.product_id.value;
        const configuration_id = this.elements.session_id.value;
        const product_data = {};
        product_data[product_id] = {
            'product_id': product_id,
            'extra': {
                'header_id': this.elements.header_id.value,
                'detail_id': this.elements.detail_id.value,
                'session_id': this.elements.session_id.value,
                'configuration_type': 'configurable'
            }
        };

        const data = {
            'results_id': 'js-design-tool-config-link',
            'product_id': product_id,
            product_data
        };

        // Send it off to the checkout and then the Design Tool in the callback.
        $.ceAjax('request', 'index.php?dispatch=checkout.add', {
            'full_render': true,
            'method': 'POST',
            'caching': false,
            'data': data,
            'message': 'Preparing Design Tool',
            'callback': (response) => {
                if (response.notifications.length === 0) {
                    window.location.href = 'index.php?dispatch=tv_designtool.artwork&configuration_id=' +
                        configuration_id + '&image_index=0&product_id=' + product_id;
                }
            }
        });
    }

    /**
     * Binds the tabs and nav buttons to the event listeners by instantiating an instance of Tabs.
     *
     * @private
     * @returns {void}
     */
    bind_to_tabs(): void {
        const tabs = document.getElementsByClassName('tv-tab__content');
        const buttons = {
            'prev': this.elements.prev,
            'next': this.elements.next,
            'links': this.elements.links,
            'mobile_links': this.elements.mobile_links
        };

        const active_tab = this.tabs ? this.tabs.get_active() : 0;
        if (this.show_hide_toggle) {
            const tab_id = <HTMLInputElement>document.getElementById('tab-id-' + active_tab);
            this.show_all_accessories(tab_id.value);
        }
        if (tabs) {
            this.tabs = new Tabs(tabs, buttons, active_tab, this.modal_suffix, this.validator, this.reconfig);
        }

        // Snap to configurator tabs when selected on product pages
        const js_config_tabs = document.getElementsByClassName("tv-tablink__mobile");
        if (js_config_tabs && !this.reconfig) {
            for (const js_config_tab of js_config_tabs) {
                js_config_tab.addEventListener('click', () => {
                    js_config_tab.scrollIntoView();
                    window.scrollBy(0, -50);
                });
            }
        }

        // Snap to configurator tabs when next and prev buttons are clicked on product pages
        const nav_buttons = document.getElementsByClassName("js-tab-nav-button");
        if (nav_buttons && !this.reconfig) {
            for (const nav_button of nav_buttons) {
                nav_button.addEventListener('click', () => {
                    // Locate the active tab link
                    let active_link = document.getElementsByClassName("js-configurator-mobile-tablink active")[0];
                    active_link.scrollIntoView();
                    window.scrollBy(0, -50);
                });
            }
        }
    }

    /**
     * Update the number input from the step button and update the configurator.
     *
     * @param {Event} event The event from the click.
     * @returns {void}
     */
    update_number_input(event): void {
        // The data attributes from the button.
        const step_data = event.target.dataset;
        const field_id = step_data.fieldId;
        const step_type = step_data.stepType;
        const step_value = parseInt(step_data.stepValue, 10);

        // Select the number input by using the field_id.
        const number_input = <HTMLInputElement>document.getElementById(field_id);

        // Determine if the adjustment value should be positive or negative based on the step type.
        const adjustment_value = (step_type === 'add') ? Math.abs(step_value) : -Math.abs(step_value);

        // Calculate the updated value with the current input value and the adjustment value.
        const updated_value = parseInt(number_input.value, 10) + adjustment_value;

        // Check the updated value against the input min and max attributes.
        if (updated_value < parseInt(number_input.min, 10)) {
            return;
        }

        number_input.setCustomValidity('');

        if (updated_value > parseInt(number_input.max, 10) || updated_value < parseInt(number_input.min, 10)) {
            if (number_input.classList.contains('js-order-quantity')) {
                let custom_message = Tygh.tr('tv_custom_validity_quantity_customer_service');

                // Replace maximum quantity
                custom_message = custom_message.replace(/{maxquantity}/g, number_input.max);

                // Replace minimum quantity
                custom_message = custom_message.replace(/{minquantity}/g, number_input.min);

                number_input.setCustomValidity(custom_message);
                number_input.reportValidity();
            }
            return;
        }

        // If T-shirt quantities are off, then we don't want to update.
        if (number_input.classList.contains('tv-t-shirt__size-input') &&
            !this.validate_t_shirt_quantities()
        ) {
            // Have to change the value back.
            number_input.value = number_input.defaultValue;
            return;
        }

        if (this.elements.t_shirt_suggestions &&
            number_input.classList.contains('js-order-quantity')
        ) {
            this.suggest_tshirt_quantity = true;
        }

        // Set the new updated value on the number input.
        number_input.value = updated_value.toString();
        number_input.defaultValue = updated_value.toString();

        /**
         * Run the configurator update unless the field being updated is the order quantity field and
         * the number of designs has been updated.  If they have then we need to pop a confirmation dialog
         * to warn the customer that changing the quantity will reset their number of designs.
         */
        if (number_input.classList.contains('js-order-quantity') &&
            this.validator.elements.number_of_designs &&
            parseInt(this.validator.elements.number_of_designs.value, 10) > 1
        ) {
            if (this.reconfig) {
                // When reconfiguring, reset the number of designs but don't show the dialog.
                this.update(number_input, true);
            } else {
                (() => new ConfirmDialog(
                    () => this.update(number_input, true),
                    null,
                    'tv_configurator_number_design_reset_confirmation',
                    true
                ))();
            }
        } else {
            this.purge_artwork = this.reconfig ? this.artwork_required : false;
            this.update(number_input);
        }
    }

    /**
     * Builds the data and makes the ajax request to update the configuration.
     *
     * @param {Array<Object<string, any>>} selections array of ID and value of fields to send to the configurator.
     * @returns {void}
     */
    configure(selections): void {
        const wishlist = <HTMLInputElement>document.getElementById('js-wishlist-id');
        const wishlist_id = wishlist ? wishlist.value : '';

        // Tell the server if this was cached and provide the cached options.
        let cached_config = "no";
        let cached_options = {};
        const cached_config_elm = <HTMLInputElement>document.getElementById('cached_config');
        const cached_options_elm = <HTMLInputElement>document.getElementById('cached_options');
        if (cached_config_elm && cached_options_elm) {
            cached_config = cached_config_elm.value;
            cached_options = cached_options_elm.value;
        }

        const data = {
            'session_id': this.elements.session_id.value,
            'product_id': this.elements.product_id.value,
            'header_id': this.elements.header_id.value,
            'detail_id': this.elements.detail_id.value,
            'selections': selections,
            'quick_view': this.quick_view,
            'reconfig': this.reconfig,
            'original_session_id': null,
            'wishlist_id': wishlist_id,
            'cached_config': cached_config,
            'cached_options': cached_options
        };

        if (this.reconfig) {
            this.modal_element = $.ceDialog('get_last')[0].parentElement;
            this.modal_style = this.modal_element.style.cssText;
            data.original_session_id = this.elements.original_session_id.value;
        }

        $.ceAjax('request', 'index.php?dispatch=tv_configurator.configure', {
            'result_ids': 'js-configurator-ui' + this.modal_suffix,
            'full_render': true,
            'method': 'POST',
            'caching': false,
            'data': data,
            'message': 'Updating Product Configuration',
            'callback': results => {
                this.handle_response(results);
            }
        });
    }

    /**
     * Disables the form inputs. WARNING this cannot be undone cleanly since some inputs are disabled from the config.
     * This is handled at the moment by just replacing the config UI in the DOM with a new one from the AJAX request.
     *
     * @private
     * @returns {void}
     */
    disable_inputs(): void {
        for (const input of this.elements.inputs) {
            input.disabled = true;
        }
    }

    /**
     * Queues a configuration with an empty selection in order to keep the connection from timing out.
     *
     * @event Configurator#keep_alive
     * @returns {void}
     */
    keep_alive(): void {
        // Close all open modals. $.ceDialog('get_last') only returns the most recently opened modal, so
        // we need to keep checking if $.ceDialog('get_last') returns an object referencing an element at index 0.
        while ($.ceDialog('get_last')[0]) {
            $.ceDialog('get_last').ceDialog('close');
        }

        this.queue_config([{}]);
    }

    /**
     * Handles keypress events in order to stop the return key from submitting the form,
     * but instead update the configuration through AJAX.
     *
     * @listens keypress
     * @param {KeyboardEvent} e event from keypress on bound input.
     * @returns {void}
     */
    keypress(e): void {
        if (e.keyCode && e.keyCode === 13) {
            e.preventDefault();
            this.validate_form();
            this.event_update(e);
        }
    }

    /**
     * Call back for CS Cart's AJAX after the configuration is updated.
     * Rebinds the controls from the new DOM elements, and updates the queue and progress flag.
     *
     * @param {Object.<string, Object>} results returned from Ajax
     * @event Configurator#handle_response
     * @returns {void}
     */
    handle_response(results): void {
        (() => new TVModal())();
        if (this.reconfig) {
            this.modal_element.style.cssText = this.modal_style;
        }

        if (results.error) {
            return;
        }

        // Reset toggle.
        this.show_hide_toggle = false;
        this.bind();
        this.set_timeout();

        let selections = [];

        // If T-shirt Product Quantity was changed, set the recommended Shirt Sizes Quantities.
        if (this.suggest_tshirt_quantity) {
            this.suggest_tshirt_quantity = false;
            selections = this.update_suggested_quantities();
        } else if (this.elements.t_shirt_suggestions) { // If T-shirt sizes where changed, set the Product Quantity.
            // The Shirt Size Quantity to Product Quantity check is in this method.
            selections = this.update_t_shirt_quantities();
        }

        // Re-bind the fields needed for Image Validation.
        this.validator.bind_all();

        // If number of designs is 1 and the quantity of designs is not equal to quantity ordered then
        // we need to update the number of designs.
        if (this.validator.elements.number_of_designs &&
            parseInt(this.validator.elements.number_of_designs.value, 10) === 1 &&
            parseInt(this.validator.elements.design_quantities[0].value, 10) !== this.validator.order_quantity
        ) {
            selections.push({
                'ID': this.validator.elements.design_quantities[0].name,
                'Value': this.validator.order_quantity
            });
        }

        // After updating the number of designs we need to update the single design quantity.
        if (this.update_designs) {
            selections.push({
                'ID': this.validator.elements.design_quantities[0].name,
                'Value': this.validator.order_quantity
            });
            this.update_designs = false;
        }

        if (selections.length > 0) {
            this.queue.push(selections);
        }

        // Run the configurator again if there are more selections in the Queue.
        if (this.queue.length > 0) {
            this.configure(this.queue.shift());
        } else { // There are no more selections to configure.
            this.config_in_progress = false;
            this.tabs.disable_tabs = false;
            this.priceUpdater.update(JSON.parse(this.elements.price_data.value));
            this.calculate_free_shipping();

            this.set_stock_status();

            // Check for smart code changes in this config if it might have artwork.
            this.check_for_smart_code_changes();

            document.getElementById('js-fastest-delivery').innerHTML = results.fastest_delivery_msg;

            if (this.reconfig) {
                this.modal_element.style.cssText = this.modal_style;
            } else {
                // Delivery Estimator Button Unhidden
                document.getElementById('js-shipping-estimator-opener-link').classList.remove('tv-display__none');
            }

            init_g_recaptcha();
            // After the Configurator finishes, re-enable the buttons.
            this.enable_buttons();
        }

        this.number_yard_letters();

        if (!this.reconfig) {
            this.sticky_cta.init();
        }

        if (this.elements.yard_shape_add_buttons || this.elements.color_picker_container) {
            this.handle_yard_letter_modals();
        }

        this.initial_calculate_fastest_delivery();

        if (this.multiple_artwork_update_in_progress === true && !this.reconfig) {
            this.validator.reset_form(true);
            document.getElementsByClassName('tv-design-options-content')[0].classList.remove('tv-display__none');
            document.getElementsByClassName('tv-multiple-design-content')[0].classList.add('tv-display__none');
            resize_last_dialog();
            this.multiple_artwork_update_in_progress = false;
        }
    }

    /**
     * Remove Wall Selection
     *
     * @param {Event} event being clicked.
     * @returns {void}
     */
    remove_shape_selection(event): void {
        this.elements.yard_shape_containers = document.getElementsByClassName('js-yard-shape-container');

        const removed_value = event.currentTarget.dataset.counter;
        const total_values = this.elements.yard_shape_containers.length;
        let selections = [];

        // Move all Yard Shapes below up one to cover the one we are removing.
        for (let i = parseInt(removed_value); i <= total_values; i++) {
            // Categories
            const currentCategory = <HTMLInputElement>document.getElementsByClassName(
                'js-yard-shape-category-' + i.toString()
            )[0];
            const nextCategory = <HTMLInputElement>document.getElementsByClassName(
                'js-yard-shape-category-' + (i + 1).toString()
            )[0];

            // This means it's the last one so we'll need to get out.
            if (!nextCategory) {
                break;
            }

            selections.push({
                'ID': currentCategory.name,
                'Value': nextCategory.value
            });

            // Shapes
            const currentShape = <HTMLInputElement>document.getElementsByClassName(
                'js-yard-shape-shape-' + i.toString()
            )[0];
            const nextShape = <HTMLInputElement>document.getElementsByClassName(
                'js-yard-shape-shape-' + (i + 1).toString()
            )[0];

            selections.push({
                'ID': currentShape.name,
                'Value': nextShape.value
            });

            // Colors
            const currentColor = <HTMLInputElement>document.getElementsByClassName(
                'js-yard-shape-color-' + i.toString()
            )[0];
            const nextColor = <HTMLInputElement>document.getElementsByClassName(
                'js-yard-shape-color-' + (i + 1).toString()
            )[0];

            selections.push({
                'ID': currentColor.name,
                'Value': nextColor.value
            });

            // Quantities
            const currentQuantity = <HTMLInputElement>document.getElementsByClassName(
                'js-yard-shape-quantity-' + i.toString()
            )[0];
            const nextQuantity = <HTMLInputElement>document.getElementsByClassName(
                'js-yard-shape-quantity-' + (i + 1).toString()
            )[0];

            selections.push({
                'ID': currentQuantity.name,
                'Value': nextQuantity.value
            });

        }
        const remove_btns = this.elements.yard_shape_remove_buttons;
        const last_remove = remove_btns.item(remove_btns.length - 1);

        // The Total Yard Shapes can not be reduced to zero unless the Message is greater than or equal to 1.
        if (total_values > 1 ||
            total_values === 1 && this.elements.custom_yard_sign_message.value.trim().length >= 1
        ) {
            // Update Number of Yard Shapes.
            selections.push({
                'ID': last_remove.dataset.id,
                'Value': parseInt(last_remove.dataset.value, 10) - 1
            });
        }

        this.queue_config(selections);

    }

    add_shape_selection(event): void {
        const option_id = event.currentTarget.dataset.id;
        const option_value = parseInt(event.currentTarget.dataset.value, 10);

        this.queue_config([{
            'ID': option_id,
            'Value': option_value + 1
        }]);
    }

    /**
     * Initializes the stock status object if it has never been initialized before.  If it has already been
     * initialized then we can just update it.
     *
     * @returns {void}
     */
    set_stock_status(): void {
        if (this.modal_suffix !== '-modal') {
            if (!this.stockStatus) {
                this.stockStatus = new StockStatus(this.elements.product_id.value, this.modal_suffix);
            } else {
                this.stockStatus.update();
            }
        }
    }

    /**
     * Compares current smart code with new smart code to see if there were any changes.  If there
     * were, we need to delete any uploaded artwork and then reinitialize the uploader.
     * Also, update the smart code for the Artwork Template Link.
     *
     * @returns {void}
     */
    check_for_smart_code_changes(): void {

        // If it exists, remove empty modal
        let dialog = document.getElementsByClassName('ui-dialog')[1];
        if (dialog) {
            dialog.remove();
        }

        const new_smart_code = (
            <HTMLInputElement>document.getElementById('js-configuration-smart-code' + this.modal_suffix)
        ).value;

        // Hide template link if there is no value to pass into the smartcode URL.
        const artwork_download_link = document.getElementById('js-download-artwork-template-link');
        if (artwork_download_link) {
            if (new_smart_code) {
                artwork_download_link.classList.remove('tv-display__none');
            } else {
                artwork_download_link.classList.add('tv-display__none');
            }
        }

        // If there was no previous smart code or our current and new smart code matches then we can just
        // reinitialize the uploaders.
        if (!this.smart_code || this.smart_code === new_smart_code) {
            this.initialize_artwork_uploader();
            return;
        }

        this.smart_code = new_smart_code;

        const data = {
            'session_id': this.elements.session_id.value,
            'can_upload_artwork': this.can_upload_artwork,
            'image_validation': null,
            'artwork_extensions': null,
            'smart_code': this.smart_code
        };

        if (this.can_upload_artwork) {
            if (data.image_validation) {
                data.image_validation = JSON.parse((
                    <HTMLInputElement>document.getElementById('js-num-of-designs-default')
                ).value);
                data.artwork_extensions = this.elements.artwork_extensions.value;
            }

            // Download Artwork template not implemented for reconfigure
            if (!this.reconfig) {
                // Download Artwork template link update.
                const download_template_url = window.location.origin +
                    '/index.php?dispatch=tv_artwork.get_template&smart_code=' + this.smart_code +
                    '&branded=1&mode=download';
                this.elements.js_download_artwork_template.setAttribute('href', download_template_url);
            }
        }

        $.ceAjax('request', 'index.php?dispatch=tv_configurator.delete_configuration_artwork_designs', {
            'result_ids': `js-configurator-artwork-upload-modal-${this.elements.session_id.value}`,
            'full_render': false,
            'method': 'POST',
            'caching': false,
            'data': data,
            'message': 'Checking Product Configuration',
            'callback': this.initialize_artwork_uploader.bind(this)
        });
    }

    /**
     * Initializes the artwork uploader object if this is a product that artwork can be uploaded to.
     *
     * @param {boolean} force_new flag for if we need to recreate the artwork uploader even if it already exists.
     * @returns {void}
     */
    initialize_artwork_uploader(force_new = false): void {
        // Only initialize the uploader if this is a product that has artwork.
        if (this.can_upload_artwork) {
            this.purge_artwork = true;
            if (!this.reconfig) {
                // If the uploader has been initialized before then we can just reiniatialize it which just
                // rebinds inputs; otherwise we need to initialize it.
                if (!this.artworkUploader || force_new) {
                    this.artworkUploader = new ConfigArtworkUploader(
                        this.elements.session_id.value,
                        this.elements.artwork_extensions.value
                    );
                } else {
                    this.artworkUploader.reinitialize();
                }

                const uploader_ids = <HTMLCollectionOf<HTMLInputElement>>document.getElementsByClassName('tv_uploader_ids');
                for (const uploader_id of uploader_ids) {
                    // This needs an uploader_id, and sales_id.
                    (() => new DIFUUploadQueue(uploader_id.value))();
                }
            }
        }
    }

    /**
     * Adds a selection the the queue and runs the next one in line if there is no config in progress.
     *
     * @private
     * @param {Array<Object<string, any>>} selections array of objects with ID and value to send to the configurator.
     * @returns {void}
     */
    queue_config(selections): void {
        // Validate inputs are not 0 in number fields.
        for (let i = 0; i < selections.length; i += 1) {
            const input = <HTMLInputElement>document.querySelector(`input[name="${selections[i].ID}"]`);
            if (input) {
                if (selections[i].Value === '' && input.type === 'number') {
                    selections[i].Value = 0;
                }
            }
        }

        this.queue.push(selections);
        if (this.config_in_progress === false) {
            this.config_in_progress = true;
            this.tabs.disable_tabs = true;
            this.disable_buttons();
            this.configure(this.queue.shift());
        }
    }

    /**
     * Replace the configuration window with the request a quote screen.
     *
     * @listens click
     * @returns {void}
     */
    request_a_quote(): void {
        // Hide the Header, Footer and Configurator Window.
        this.elements.pricing_heading.style.display = 'none';

        if (this.modal_suffix !== '-modal') {
            this.elements.product_footer.style.display = 'none';
        }

        this.elements.main_ui.classList.add('hidden');
        this.elements.config_summary.classList.add('hidden');

        const data = {
            'results_id': 'js-product-quote-window',
            'session_id': this.elements.session_id.value,
            'product_id': this.elements.product_id.value,
            'header_id': this.elements.header_id.value,
            'detail_id': this.elements.detail_id.value
        };

        // Populate the quote div.
        $.ceAjax('request', 'index.php?dispatch=tv_product_quotes.load_request_a_quote_window', {
            'full_render': true,
            'method': 'POST',
            'caching': false,
            'data': data,
            'message': 'Loading Quote Information',
            'callback': () => {
                (() => new StateSelectorView())();

                this.elements.return_to_configurator = document.getElementById('js-return-to-configuration');
                this.elements.return_to_configurator.addEventListener('click', this.return_to_configurator.bind(this));

                // Scroll to the quote div.
                const quote_window = document.getElementById('js-product-quote-window');
                quote_window.scrollIntoView();

                init_g_recaptcha();
            }
        });
    }

    /**
     * Save the wall option that was selected the tent wall modal.
     *
     * @param {Event} event The event from the click.
     * @returns {void}
     */
    save_wall_selection(event): void {
        const this_target = event.target;
        // Find the checked input and run queue config to update the configurator.
        if (this_target.classList.contains('js-tent__walls-remove-input')) {
            this.selected_wall_input = this_target;
        }
        if (this.selected_wall_input.checked) {
            const selection = {
                'ID': this.selected_wall_input.dataset.id,
                'Value': this.selected_wall_input.value
            };
            this.queue_config([selection]);
        }
    }

    /**
     * Select a wall option.
     *
     * @param {Event} event The event from the click.
     * @returns {void}
     */
    select_wall_input(event): void {
        for (const tent_wall_option of this.elements.tent_walls_options) {
            tent_wall_option.querySelector(
                '.tv-tent__walls-selection-indicator'
            ).classList.remove('tv-visibility__visible');
            tent_wall_option.querySelector(
                '.tv-tent__walls-selection-image'
            ).classList.remove('tv-filter__50');
        }
        this.selected_wall_input = event.target;
        const save_wall_button = document.getElementById(`save-wall-button-${this.selected_wall_input.dataset.id}`);
        if (this.selected_wall_input.checked) {
            save_wall_button.classList.remove('tv-disabled__button');
        }
    }

    /**
     * Replaces the request a quote screen with the configuration window.
     *
     * @listens click
     * @returns {void}
     */
    return_to_configurator(): void {
        // Show the Header and Footer.
        this.elements.pricing_heading.style.display = 'block';

        if (this.modal_suffix !== '-modal') {
            this.elements.product_footer.style.display = 'block';
        }

        this.elements.main_ui.classList.remove('hidden');

        // If the item has already been added to the cart display the config summary; otherwise,
        // display the config ui.
        if (this.added_to_cart) {
            this.elements.config_summary.classList.remove('hidden');
        }

        // Wipe out the quote screen
        this.elements.quote_window.innerHTML = '';

    }

    /**
     * Clears the current keep alive timeout if set, starts a new one.
     *
     * @private
     * @returns {void}
     */
    set_timeout(): void {
        if (this.configuration_type !== 'hardware') {
            if (this.timeout) {
                clearInterval(this.timeout);
            }
            this.timeout = setInterval(this.keep_alive.bind(this), config().configurator.timeout);
        }
    }

    /**
     * Update the configurator with the element from the change event.
     *
     * @param {Event} e The event from the element that changed.
     * @returns {void}
     */
    event_update(e): void {
        const element = e.target || e.srcElement;
        this.disable_buttons();

        // Validate custom yard sign message.
        if ((element.classList.contains('js-tv-custom-message-yard-sign') ||
            element.classList.contains('tv-custom-message-yard-sign__input')) &&
            !this.validate_custom_yard_sign_message(element)
        ) {
            // Have to change the value back.
            element.reportValidity();
            e.preventDefault();
            e.stopPropagation();
            this.enable_buttons();
            return;
        }

        // If T-shirt quantities are off, then we don't want to update.
        if (element.classList.contains('tv-t-shirt__size-input') && !this.validate_t_shirt_quantities()) {
            // Have to change the value back.
            element.value = element.defaultValue;
            e.preventDefault();
            e.stopPropagation();
            this.enable_buttons();
            return;
        }

        if (this.elements.t_shirt_suggestions && element.classList.contains('js-order-quantity')) {
            this.suggest_tshirt_quantity = true;
        }

        this.elements.quantity_input = document.getElementsByClassName('js-order-quantity');
        this.elements.quantity_input[0].setCustomValidity('');

        /**
         * Run the configurator update unless the field being updated is the order quantity field and
         * the number of designs has been updated.  If they have then we need to pop a confirmation dialog
         * to warn the customer that changing the quantity will reset their artwork.
         */
        element.defaultValue = element.value;
        if (element.classList.contains('js-order-quantity')
            && this.validator.elements.number_of_designs
            && parseInt(this.validator.elements.number_of_designs.value, 10) > 1
        ) {
            if (this.reconfig) {
                this.update(element, true);
            } else {
                (() => new ConfirmDialog(
                    () => this.update(element, true),
                    null,
                    'tv_configurator_number_design_reset_confirmation',
                    true
                ))();
            }
        } else {
            this.purge_artwork = this.reconfig ? this.artwork_required : false;
            this.update(element);
        }
    }

    /**
     * Checks to make sure all form elements are valid and if there is one that isn't
     * it sets the focus to that field and disables all other fields.
     *
     * @returns {void}
     */
    validate_form(): void {
        let input_valid = true;
        // Check all inputs for validity, set the focus to the invalid input,
        // and lock down all other elements.
        for (const input of this.elements.inputs) {
            if (!input.validity.valid) {
                // Close all open modals before changing Focus.
                while ($.ceDialog('get_last')[0]) {
                    const open_modal = $.ceDialog('get_last');
                    // Prevent reconfig modal from closing.
                    if (this.reconfig && open_modal.selector === '#js-configurator-ui-modal') {
                        break;
                    }
                    open_modal.ceDialog('close');
                }

                input.focus();
                input.reportValidity();
                input_valid = false;
            } else {
                input.disabled = true;
            }
        }

        // If all form elements are valid then we need to make sure all of the
        // elements are enabled.
        if (input_valid) {
            for (const input of this.elements.inputs) {
                input.disabled = false;
            }
        }

        // Wait to see if a Configuration begins.
        setTimeout(() => {
            if (!this.config_in_progress) {
                // If no Configuration is started, then enable the buttons.
                this.enable_buttons();
            }
        }, 125); // 1/8 second.
    }

    /**
     * Creates a thumbnail for users to click to go to the videos tab, if videos are present on this page
     *
     * @returns {void}
     */
    create_video_thumbnail(): void {

        // Create Video Thumbnail
        const product_thumbnail_container = document.querySelector('.ty-product-thumbnails .owl-wrapper');
        const video_thumbnail_image = document.getElementsByClassName('youtube-player')[0].children[0].children[0].getAttribute('src');
        const video_thumbnail = document.createElement("DIV");
        const video_play_icon = document.createElement("DIV");

        video_thumbnail.classList.add('ty-product-thumbnails__item');
        video_thumbnail.classList.add('tv-video-thumbnail');
        video_thumbnail.style.backgroundImage = 'url(' + video_thumbnail_image + ')';
        video_play_icon.classList.add('tv-video-thumbnail-play-btn');

        if (product_thumbnail_container) {
            product_thumbnail_container.appendChild(video_thumbnail);
        } else {
            return;
        }
        video_thumbnail.appendChild(video_play_icon);

        if (window.innerWidth >= 767) {
            // Click functionality for desktop video thumbnail
            const tv_tabs = Array.from(<HTMLCollectionOf<HTMLElement>>document.getElementsByClassName('ty-tabs__item'));
            video_thumbnail.addEventListener('click', () => {
                tv_tabs.forEach(tv_tab => {
                    if (tv_tab.innerText == 'Videos') {
                        tv_tab.click();
                        tv_tab.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
                    }
                });
            });
        } else {
            // Click functionality for mobile video thumbnail
            const tv_mobile_tabs = Array.from(
                <HTMLCollectionOf<HTMLElement>>document.getElementsByClassName('ui-accordion-header')
            );
            video_thumbnail.addEventListener('click', () => {
                tv_mobile_tabs.forEach(tv_mobile_tab => {
                    if (tv_mobile_tab.innerText == 'Videos') {
                        tv_mobile_tab.click();
                        tv_mobile_tab.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
                    }
                });
            });
        }

    }

    /**
     * Updates the number of designs for the config from the image validation modal.
     *
     * @returns {void}
     */
    update_number_of_designs(): void {
        this.purge_artwork = this.reconfig ? this.artwork_required : false;
        this.multiple_artwork_update_in_progress = true;
        const selections = [{
            'ID': this.validator.elements.number_of_designs.name,
            'Value': this.validator.elements.number_of_designs.value
        }];
        const data = {
            'session_id': this.elements.session_id.value,
            'product_id': this.elements.product_id.value,
            'header_id': this.elements.header_id.value,
            'detail_id': this.elements.detail_id.value,
            'selections': selections,
            'quick_view': this.quick_view,
            'reconfig': this.reconfig
        };

        $.ceAjax('request', 'index.php?dispatch=tv_configurator.update_number_of_designs', {
            'full_render': true,
            'method': 'POST',
            'caching': false,
            'data': data,
            'message': 'Updating Number of Designs',
            'callback': (response) => {
                this.update_quantity_design(response);
                if (this.reconfig) {
                    // Wait for config queue to finish before saving.
                    $.ceEvent('on', 'ce.ajaxdone', () => {
                        if (!this.config_in_progress && this.multiple_artwork_update_in_progress) {
                            this.multiple_artwork_update_in_progress = false;
                            this.save_reconfig();
                        }
                    });
                }
            }
        });
    }

    /**
     * Call back for CS Cart's AJAX after the number of designs has been updated.
     * Goes through each design quantity field from the configurator and uses the
     * id with the the design quantity field from the image validation modal's value.
     *
     * @param {Object} data with config image validation information.
     * @returns {void}
     */
    update_quantity_design(data): void {
        const image_validation = data.image_validation;
        const selections = [];

        // If number of designs is 1, we need to set the first design quantity to the order quantity.
        if (parseInt(image_validation.number_of_designs.value, 10) === 1) {
            selections.push({
                'ID': image_validation.design_quantities[0].id,
                'Value': image_validation.order_quantity
            });
        } else {
            for (let i = 0; i < image_validation.design_quantities.length; i += 1) {
                selections.push({
                    'ID': image_validation.design_quantities[i].id,
                    'Value': this.validator.elements.design_quantities[i].value
                });
            }
        }

        this.queue_config(selections);
        this.disable_inputs();
    }

    /**
     * Queues a configuration with the selection from the UI controls.
     *
     * @listens change
     * @event Configurator#update
     * @param {HTMLInputElement} element the input that was changed.
     * @param {boolean} reset_num_designs flag for whether number of designs field should be reset to 1.
     * @returns {void}
     */
    update(element, reset_num_designs = false): void {
        for (const input of this.elements.inputs) {
            // Default blank number inputs to zero so they will validate.
            if (input.value === '' && input.type === 'number') {
                input.value = 0;
            }

            if (!input.validity.valid) {
                if (input.classList.contains('js-order-quantity')) {
                    let custom_message = Tygh.tr('tv_custom_validity_quantity_customer_service');

                    // Replace maximum quantity
                    custom_message = custom_message.replace(/{maxquantity}/g, input.max);

                    // Replace minimum quantity
                    custom_message = custom_message.replace(/{minquantity}/g, input.min);

                    input.setCustomValidity(custom_message);
                }
                input.reportValidity();
                return;
            }
        }

        // CSCART 4 TODO - Commenting this out so that hardware only pricing will update when the
        // quantity is changed. Can add all of the quantity discounts in the hardware configuration
        // so that we don't have to run the whole configuration through each time the quantity is changed.
        /* if (this.configuration_type === 'hardware') {
            return;
        }*/
        const selections = [{
            'ID': element.name,
            'Value': element.value
        }];

        if (reset_num_designs) {
            this.update_designs = true;
            this.purge_artwork = true;

            selections.push({
                'ID': this.validator.elements.number_of_designs.name,
                'Value': '1'
            });
        }

        // Check if the element has the group update class on it and if it does we need to pull values
        // from any other element that has that class and add it to the update data.
        if (element.classList.contains('js-config-group-update')) {
            const grouped_items = <HTMLCollectionOf<HTMLInputElement>>document.getElementsByClassName(
                'js-config-group-update'
            );
            for (const item of grouped_items) {
                if (item === element) {
                    continue;
                }

                selections.push({
                    'ID': item.name,
                    'Value': item.value
                });
            }
        }

        // Throttle user inputs so that if they are rapidly making changes to a control we don't send ajax too soon.
        if (this.throttle !== null) {
            clearTimeout(this.throttle);
        }

        this.throttle = setTimeout(() => {
            this.disable_inputs();
            this.queue_config(selections);
        }, 500);
    }
}

